<template>
  <div class="see">
    <div class="see__rows">
      <div v-for="(entry, i) in value" :key="i" class="see-row">
        <div class="see-row__value">
          <span class="see-row__field-label">Label</span>
          <v-text-field
            :value="entry.value"
            placeholder="e.g. High"
            maxlength="120"
            outlined
            dense
            hide-details
            :aria-label="`Value for label ${i + 1}`"
            @change="setValue(i, $event)">
          </v-text-field>
        </div>

        <div class="see-row__paint">
          <span class="see-row__field-label">Color</span>
          <schema-color-field
            :value="entry.color"
            :aria-label="`Choose color for label ${i + 1}`"
            hint="Choose any color for this label."
            @input="setColor(i, $event)">
          </schema-color-field>
        </div>

        <v-btn
          icon
          small
          class="see-row__remove"
          :disabled="value.length <= 1"
          :title="value.length <= 1 ? 'A schema needs at least one label' : 'Remove label'"
          @click="removeEntry(i)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="see__actions">
      <v-btn text small color="primary" @click="addEntry">
        <v-icon small left>mdi-plus</v-icon> Add label
      </v-btn>
    </div>
  </div>
</template>

<script>
import SchemaColorField from './SchemaColorField.vue';

// Discrete label→color rows for a category schema. Unlike SchemaBandsEditor the rows are
// INDEPENDENT — each label carries its own raw color pair, with no connected boundaries.
const DEFAULT_COLOR = Object.freeze({ light: '#a07a00', dark: '#a07a00' }); // caution amber

export default {
  name: 'SchemaEntriesEditor',

  components: { SchemaColorField },

  props: {
    value: { type: Array, default: () => [] },
  },

  methods: {
    emitEntries(entries) {
      this.$emit('input', entries);
    },

    addEntry() {
      this.emitEntries([...this.value, { value: '', color: { ...DEFAULT_COLOR } }]);
    },

    removeEntry(i) {
      if (this.value.length <= 1) return;
      this.emitEntries(this.value.filter((_, index) => index !== i));
    },

    patchEntry(i, patch) {
      this.emitEntries(this.value.map((entry, index) => (index === i ? { ...entry, ...patch } : entry)));
    },

    setValue(i, value) {
      this.patchEntry(i, { value });
    },

    setColor(i, pair) {
      this.patchEntry(i, { color: pair });
    },
  },
};
</script>

<style lang="scss" scoped>
.see { display: flex; flex-direction: column; gap: 12px; }
.see__rows { overflow: hidden; border: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08)); border-radius: var(--ds-radius-md, 8px); }
.see-row { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(146px, .8fr) 28px; gap: 12px; align-items: end; padding: 12px; }
.see-row + .see-row { border-top: 1px solid var(--ds-border-subtle, rgba(0, 0, 0, 0.08)); }
.see-row__field-label { display: block; margin-bottom: 6px; color: #52615c; font-size: 11px; font-weight: 700; letter-spacing: .045em; text-transform: uppercase; }
.see-row__remove { margin-bottom: 4px; color: #52615c; }
.see-row__remove:hover:not(.v-btn--disabled) { color: var(--v-error-base); background: rgba(183, 28, 28, 0.08); }
.see-row__remove:focus-visible { outline: 2px solid var(--ds-focus-ring-color, #2cc295); outline-offset: 2px; }
.see__actions { display: flex; align-items: center; min-height: 36px; }
.see__actions .v-btn { min-height: 36px; padding: 0 10px; font-size: 13px; font-weight: 600; text-transform: none; letter-spacing: normal; }
.see__actions .v-btn:focus-visible { outline: 2px solid var(--ds-focus-ring-color, #2cc295); outline-offset: 2px; }
.theme--dark .see__rows, .theme--dark .see-row + .see-row { border-color: rgba(255, 255, 255, 0.1); }
.theme--dark .see-row__field-label, .theme--dark .see-row__remove { color: rgba(255, 255, 255, 0.78); }
@media (max-width: 720px) { .see-row { grid-template-columns: 1fr 28px; gap: 10px; } .see-row__value, .see-row__paint { grid-column: 1; } .see-row__remove { grid-column: 2; grid-row: 1; } }
</style>
